import React from 'react';

const JobCardSkeleton = ({ delay = 0, viewMode = 'grid' }) => {
  
  // List view layout skeleton
  if (viewMode === 'list') {
    return (
      <div 
        className="bg-surface rounded-lg border border-border hover:border-primary transition-colors overflow-hidden animate-pulse"
        style={{ animationDelay: `${delay}ms` }}
      >
        <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3">
              {/* Company Logo Skeleton */}
              <div className="h-12 w-12 rounded bg-gradient-to-r from-gray-200 to-gray-300"></div>
              <div className="flex-1 min-w-0">
                {/* Job Title */}
                <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2 w-3/4"></div>
                {/* Company Name */}
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2 w-1/2"></div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                  {/* Location */}
                  <div className="h-3 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  {/* Job Type */}
                  <div className="h-3 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  {/* Budget */}
                  <div className="h-3 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
              </div>
            </div>
            
            {/* Skills Tags */}
            <div className="mt-4 flex flex-wrap gap-2">
              {[1, 2, 3].map((index) => (
                <div
                  key={index}
                  className="h-6 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"
                ></div>
              ))}
              <div className="h-6 w-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
            </div>
          </div>
          <div className="flex md:flex-col items-center gap-3 mt-4 md:mt-0">
            {/* Apply Button */}
            <div className="h-8 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            {/* Bookmark Button */}
            <div className="h-8 w-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view layout skeleton (default)
  return (
    <div 
      className="bg-surface rounded-lg border border-border p-6 hover:shadow-md transition-all duration-200 animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {/* Company Logo Skeleton */}
          <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300 flex-shrink-0"></div>
          <div className="flex-1 min-w-0">
            {/* Job Title */}
            <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2 w-3/4"></div>
            {/* Company Name */}
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-1 w-1/2"></div>
            <div className="flex items-center space-x-4 mt-1">
              {/* Location */}
              <div className="h-3 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              {/* Remote Badge */}
              <div className="h-4 w-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
            </div>
          </div>
        </div>
        
        {/* Bookmark Icon */}
        <div className="h-8 w-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg flex-shrink-0"></div>
      </div>

      {/* Description */}
      <div className="mb-4 space-y-2">
        <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
        <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-4/5"></div>
      </div>

      {/* Job Details */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          {/* Salary */}
          <div className="h-4 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          {/* Job Type */}
          <div className="h-4 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
        </div>
        {/* Experience Level */}
        <div className="h-5 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
      </div>

      {/* Skills Match (sometimes shown) */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="h-3 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          <div className="h-5 w-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
        </div>
        <div className="w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded-full h-2"></div>
      </div>

      {/* Skills Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[1, 2, 3].map((index) => (
          <div
            key={index}
            className="h-6 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md"
          ></div>
        ))}
        <div className="h-6 w-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md"></div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          {/* Posted Time */}
          <div className="h-3 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
        </div>
        
        {/* Apply Button */}
        <div className="h-8 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
      </div>
    </div>
  );
};

export default JobCardSkeleton;
